import { useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"

import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { getScannerConnectionStatus } from "@/core/app/slices/integrations/integrationsThunkApi"
import {
  isRegisteredScannerOAuthId,
  isSameScannerId,
} from "@/core/app/slices/integrations/scannerConstants"

import {
  notifyOpenerAndCloseScanner,
  parseScannerOAuthQuery,
  stripScannerOAuthParams,
  type ParsedScannerOAuth,
} from "./scannerOAuth"

export interface ScannerOAuthQueryHandlerOptions {
  /** Integrations card: only this scanner. Callback route: omit — URL must list a registered scanner. */
  scannerId?: string
  /** Integrations page: refetch when URL has no OAuth params. Callback route: set `false`. */
  refetchWhenNoOAuthParam?: boolean
}

function isOAuthReturnForThisContext(
  parsed: NonNullable<ParsedScannerOAuth>,
  fixedScannerId: string | undefined,
): boolean {
  if (fixedScannerId != null) {
    return isSameScannerId(parsed.scanner, fixedScannerId)
  }
  return isRegisteredScannerOAuthId(parsed.scanner)
}

/**
 * React to OAuth return query params. Today: `?easyrx=true|false` only (see `parseScannerOAuthQuery`).
 * Strips params and notifies the popup opener (postMessage / BroadcastChannel).
 */
export function useScannerOAuthQueryHandler(
  options: ScannerOAuthQueryHandlerOptions,
): void {
  const optsRef = useRef(options)
  optsRef.current = options

  const dispatch = useAppDispatch()
  const orgId = useAppSelector((s) => s.userService.user.current_orgId)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (!orgId) return

    const {
      scannerId: fixedScannerId,
      refetchWhenNoOAuthParam = true,
    } = optsRef.current

    const parsed = parseScannerOAuthQuery(searchParams)

    if (parsed == null) {
      if (refetchWhenNoOAuthParam && fixedScannerId != null) {
        dispatch(getScannerConnectionStatus())
      }
      return
    }

    if (!isOAuthReturnForThisContext(parsed, fixedScannerId)) return

    const { scanner, ok } = parsed
    stripScannerOAuthParams(searchParams)

    if (ok) {
      notifyOpenerAndCloseScanner(scanner, true)
      return
    }

    notifyOpenerAndCloseScanner(scanner, false)
  }, [orgId, searchParams, dispatch])
}
